import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { Cropper as AdvancedCropper } from 'react-advanced-cropper';
import { Icon } from '@iconify/react';
import PerspectiveCropperCanvas from '../PerspectiveCropperCanvas/PerspectiveCropperCanvas';
import FreeCropCanvas from '../FreeCropCanvas/FreeCropCanvas';
import type { UploadReceiptPreviewPanelProps } from '../../../types/uploadReceiptDialog';

const UploadReceiptPreviewPanel = ({
  selectedFile,
  previewUrl,
  editMode,
  modeSwitching,
  perspectiveLoading,
  perspectiveKey,
  rotation,
  cropperRef,
  perspectiveCropperRef,
  freeCropRef,
  onCropperChange,
  onPerspectiveState,
}: UploadReceiptPreviewPanelProps) => {
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Paper
        elevation={1}
        sx={{
          minHeight: '90%',
          height: '100%',
          alignSelf: 'stretch',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: selectedFile ? 'flex-start' : 'center',
          alignItems: 'center',
          textAlign: 'center',
          bgcolor: '#2C6CA00A',
        }}
      >
        {!selectedFile && (
          <Box
            sx={{
              display: 'flex',
              flex: '1 1 auto',
              width: '100%',
              minHeight: 0,
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Icon icon="ic:baseline-block" width="60" height="60" style={{ color: '#2C6CA0' }} />
            <Typography variant="body1" sx={{ fontSize: 24, fontWeight: 600, color: '#222B45' }}>
              NO FILE SELECTED
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 18, fontWeight: 400, color: 'grey' }}>
              Please select a file to preview the receipt.
            </Typography>
          </Box>
        )}
        {selectedFile && (
          <>
            {modeSwitching && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Applying changes...
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flex: '1 1 auto', width: '100%', minHeight: 0 }}>
              {editMode === 'none' && previewUrl && selectedFile && (
                <Box
                  sx={{
                    width: '100%',
                    flex: '1 1 auto',
                    minHeight: 0,
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Selected receipt preview"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>
              )}
              {editMode === 'tilt' && (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    flex: '1 1 auto',
                    minHeight: 0,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <AdvancedCropper
                    key={`tilt-${perspectiveKey}-${rotation}`}
                    ref={cropperRef}
                    src={previewUrl}
                    className="rac-cropper"
                    onChange={onCropperChange}
                    defaultTransforms={{
                      rotate: rotation,
                    }}
                    defaultSize={{
                      width: 4096,
                      height: 8192,
                    }}
                    stencilProps={{
                      grid: true,
                    }}
                  />
                </Box>
              )}
              {editMode === 'perspective' && previewUrl && selectedFile && (
                <Box sx={{ position: 'relative', width: '100%', flex: '1 1 auto', minHeight: 0 }}>
                  <PerspectiveCropperCanvas
                    key={`perspective-${perspectiveKey}`}
                    ref={perspectiveCropperRef}
                    imageUrl={previewUrl}
                    file={selectedFile}
                    onChange={onPerspectiveState}
                    onDragStop={onPerspectiveState}
                  />
                  {(perspectiveLoading || modeSwitching) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Preparing perspective view...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {editMode === 'free' && previewUrl && selectedFile && (
                <Box sx={{ position: 'relative', width: '100%', flex: '1 1 auto', minHeight: 0 }}>
                  <FreeCropCanvas
                    key={`free-${perspectiveKey}`}
                    ref={freeCropRef}
                    imageUrl={previewUrl}
                    file={selectedFile}
                    onChange={onPerspectiveState}
                  />
                  {(perspectiveLoading || modeSwitching) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Processing...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default UploadReceiptPreviewPanel;
